/**
 * Pure helpers for Create → Translate: the run's result shape, which documents
 * the form may offer (originals only, indexed, a translatable format, under the
 * size cap), and labels. Free of React so they are unit-tested directly.
 */

import { extensionOf } from "@/features/collections/collectionMeta";
import type { IndexedDocumentOut } from "@/shared/api/indexes";

/** `params` of a `translate` run (backend `TranslateParams`). */
export interface TranslateParams {
  target_language: string;
  /** `null` = auto-detect. */
  source_language: string | null;
}

export interface TranslatedDocument {
  document_id: string;
  display_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  content_hash: string | null;
  page_count: number | null;
  language: string | null;
}

/** `result` of a succeeded `translate` run (backend `TranslateResult`). The
 *  run API types `result` as an open object; this is the kind's shape. */
export interface TranslateResult {
  index_id: string;
  index_name: string;
  original: TranslatedDocument;
  translation: TranslatedDocument;
  source_language: string | null;
  target_language: string;
  characters_charged: number;
  service: string;
  api_version: string;
  replaced_previous: boolean;
  build: { status: "started" | "unavailable" | "busy" | string; message: string };
  ai_inference_ids?: string[];
}

export function asTranslateResult(result: unknown): TranslateResult | null {
  if (!result || typeof result !== "object") return null;
  const r = result as Partial<TranslateResult>;
  return r.original && r.translation && typeof r.target_language === "string"
    ? (r as TranslateResult)
    : null;
}

export function isTranslation(d: IndexedDocumentOut): boolean {
  return d.source_kind === "derived" || Boolean(d.derived_from_document_id);
}

export interface TranslateCandidate {
  doc: IndexedDocumentOut;
  /** Upper-case language codes of existing translations, e.g. ["DE", "FR"]. */
  translations: string[];
  /** Why it cannot be picked, or `null`. */
  disabledReason: string | null;
}

/** The originals of a collection, with why each can or cannot be translated. */
export function translateCandidates(
  docs: IndexedDocumentOut[],
  extensions: readonly string[],
  maxBytes: number,
): TranslateCandidate[] {
  const byOriginal = new Map<string, string[]>();
  for (const d of docs) {
    if (d.derived_from_document_id && d.language) {
      const list = byOriginal.get(d.derived_from_document_id) ?? [];
      list.push(d.language.toUpperCase());
      byOriginal.set(d.derived_from_document_id, list);
    }
  }
  return docs
    .filter((d) => !isTranslation(d))
    .map((doc) => ({
      doc,
      translations: (byOriginal.get(doc.document_id) ?? []).sort(),
      disabledReason: unavailableReason(doc, extensions, maxBytes),
    }));
}

function unavailableReason(
  doc: IndexedDocumentOut,
  extensions: readonly string[],
  maxBytes: number,
): string | null {
  if (doc.status !== "indexed") return `not indexed (${doc.status})`;
  // Veeva documents are read as their rendition (usually a PDF): the stored
  // name may carry no extension, so the backend checks once it has the file.
  if (doc.source_kind !== "veeva") {
    const ext = extensionOf(doc.source_path ?? doc.display_name);
    if (!extensions.includes(ext)) return `${ext || "this format"} cannot be translated`;
  }
  if (doc.size_bytes != null && doc.size_bytes > maxBytes) {
    return `larger than ${Math.round(maxBytes / (1024 * 1024))} MB`;
  }
  return null;
}

export function isPdf(doc: Pick<IndexedDocumentOut, "display_name" | "source_path">): boolean {
  return extensionOf(doc.source_path ?? doc.display_name) === ".pdf";
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
